import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { StainType } from 'interfaces/stainType';
import { find, findKey, first, groupBy, includes, join, map, size, sortBy } from 'lodash';
import React from 'react';
import { PanelSlideSummary } from './PanelSlideSummary';

export const PanelChannelSummary = ({
  panels,
  stains,
  labId,
}: {
  panels: MultiplexPanelView[];
  stains: StainType[];
  labId?: string;
}) => (
  <Stack spacing={2} aria-label="Multiplex panel channel summaries">
    {map(panels, (panel) => (
      <Stack key={panel.panelId} spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="subtitle2">{panel.name || panel.panelId.replace('panel-', 'Panel ')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {panel.slides.length} slides · {panel.channels.length} channels ·{' '}
            {size(panel.slides.filter((slide) => slide.approvalStatus === 'approved'))} approved
          </Typography>
          {!panel.saved && <Chip size="small" label="Not defined" />}
        </Stack>
        <TableContainer>
          <Table size="small" aria-label={`${panel.panelId} channel summary`}>
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Current settings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(sortBy(panel.channels, 'channelIndex'), (channel) => {
                const assignments = map(panel.slides, (slide) => {
                  const channelId = findKey(slide.channelMapping, (index) => index === channel.channelIndex);
                  const markerType = channelId === undefined ? undefined : slide.channelMarkerTypes[channelId];
                  const stain = find(stains, { id: markerType });
                  const isRna = channelId !== undefined && includes(slide.rnaChannelIds, channelId);
                  const modality = isRna ? 'RNA' : !markerType ? 'Unassigned' : stain?.dna ? 'DNA' : 'mIF';
                  const label = !markerType
                    ? 'Missing marker name'
                    : channel.saved?.analysisName === markerType
                    ? channel.saved.displayName || stain?.displayName || markerType
                    : stain?.displayName || markerType;
                  return { markerType, modality, label };
                });
                const groups = groupBy(assignments, (assignment) =>
                  JSON.stringify([assignment.markerType, assignment.modality])
                );
                return (
                  <TableRow key={channel.channelIndex}>
                    <TableCell>{channel.channelIndex}</TableCell>
                    <TableCell>{join(channel.identifiers.sourceNames, ' / ')}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {map(groups, (members, key) => (
                          <Chip
                            key={key}
                            size="small"
                            variant="outlined"
                            label={`${first(members)?.label} · ${first(members)?.modality}${
                              members.length === panel.slides.length
                                ? ''
                                : ` · ${members.length} ${members.length === 1 ? 'slide' : 'slides'}`
                            }`}
                          />
                        ))}
                        {size(groups) > 1 && (
                          <Typography variant="caption" color="text.secondary">
                            Mixed current settings
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <PanelSlideSummary panel={panel} labId={labId} />
      </Stack>
    ))}
  </Stack>
);
